import { MediaFragment } from "@src/fragments.generated";
import { FC, useMemo } from "react";

import { getMediaUrl } from "@src/utils/media";
import { getLinkType, LinkType } from "@src/utils/files";
import VideoPlayer from "@src/deprecatedDesignSystem/components/VideoPlayer";
import PhotoOutlineIcon from "@src/ui/icons/18px/photo-outline";
import LinkIcon from "@src/ui/icons/18px/link";
import VolumeUpOutlineIcon from "@src/ui/icons/18px/volume-up-outline";
import VideoOutlineIcon from "@src/ui/icons/18px/video-outline";
import UserOutlineIcon from "@src/ui/icons/18px/user-outline";
import FileTextOutlineIcon from "@src/ui/icons/18px/file-text-outline";
import CircleQuestionOutlineIcon from "@src/ui/icons/18px/circle-question-outline";

type Props = {
  media: MediaFragment;
  ogImage?: string;
  className?: string;
};

export const ResourceThumbnail: FC<Props> = (props) => {
  const url = getMediaUrl(props.media);
  const linkType = useMemo(() => getLinkType(url), [url]);
  return useMemo(() => {
    if (
      [LinkType.IMAGE, LinkType.GIF].includes(linkType) ||
      props.media.thumbnailImageUrl
    ) {
      return (
        <ResourceImage src={props.media.thumbnailImageUrl || url} alt={""} />
      );
    }
    if ([LinkType.VIDEO, LinkType.MUX_VIDEO].includes(linkType)) {
      return <Video src={url} />;
    }
    if (props.ogImage) {
      return <ResourceImage src={props.ogImage} alt={""} />;
    }
    return (
      <div className="flex h-[93px] w-[91px] items-center justify-center">
        <ResourceIcon linkType={linkType} />
      </div>
    );
  }, [linkType, props.media.thumbnailImageUrl, url, props.ogImage]);
};

const ResourceImage: FC<{ src: string; alt: string }> = (props) => {
  return (
    <img className="size-full object-contain" src={props.src} alt={props.alt} />
  );
};

const ResourceIcon: FC<{ linkType: LinkType }> = (props) => {
  if (props.linkType === LinkType.PDF) {
    return <FileTextOutlineIcon />;
  }
  if ([LinkType.GIF, LinkType.IMAGE].includes(props.linkType)) {
    return <PhotoOutlineIcon />;
  }
  if (
    [LinkType.VIDEO, LinkType.MUX_VIDEO, LinkType.HOSTED_VIDEO].includes(
      props.linkType,
    )
  ) {
    return <VideoOutlineIcon />;
  }
  if (props.linkType === LinkType.LINK) {
    return <LinkIcon />;
  }
  if (props.linkType === LinkType.AUDIO) {
    return <VolumeUpOutlineIcon />;
  }
  if (props.linkType === LinkType.VCARD) {
    return <UserOutlineIcon />;
  }
  return <CircleQuestionOutlineIcon />;
};

const Video: FC<{
  src: string;
}> = (props) => {
  return (
    <VideoPlayer
      hideControls
      playOnHover
      videoProps={{
        loop: true,
        className: "h-full w-full object-contain",
      }}
      src={props.src}
    />
  );
};
